import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DialogType } from '../../../@types';
import { SelectionsBonusCampaign } from '../../../@types/api';
import {
  BONUS_PRODUCT_TYPE_OPTIONS,
  BONUS_STATUS_OPTIONS_ONGOING,
  QUERY_KEYS,
  SELECTION_BONUS_ELIGIBILITY_OPTIONS,
} from '../../../constants';
import { useModal } from '../../../contexts/ModalContext';
import useMutateData from '../../../hooks/useMutateData';
import { SelectionBonusFormData, selectionBonusFormSchema } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import CustomerSelect from '../../molecules/CustomerSelect';
import FormSelect from '../../molecules/FormSelect';
import ValidityPeriodSelect from '../../molecules/ValidityPeriodSelect';
import { BonusPerSelectedCombinations } from '../BonusPerSelectedCombinations';
import FormNumberInput from '../../molecules/FormNumberInput';
import { mapPairsToArray, toEntryMap } from '../../../utils/selectionBonusForm';
import { DEFAULT_SELECTION_BONUS_FORM_DATA } from '../../../constants/defaultFormValues';
import { getDirtyFieldValues } from '../../../utils/form';
import { formatSnakeCaseString } from '../../../utils';

type Props = DialogType;

const NumOfSelectionsBonusForm = ({ closeModal }: Props) => {
  const { item } = useModal<SelectionsBonusCampaign>();
  const { createData, updateData } = useMutateData('bonus-campaigns', [QUERY_KEYS.bonus]);

  const isEditMode = !!item;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    watch,
  } = useForm<input<typeof selectionBonusFormSchema>, unknown, SelectionBonusFormData>({
    defaultValues: DEFAULT_SELECTION_BONUS_FORM_DATA,
    resolver: zodResolver(selectionBonusFormSchema),
  });

  const handleClose = () => {
    closeModal?.();
    reset(DEFAULT_SELECTION_BONUS_FORM_DATA);
  };

  const onFormSubmit = (data: SelectionBonusFormData) => {
    const payload = {
      ...data,
      name: formatSnakeCaseString(data.numOfSelectionsBonusEligibility),
      bonusPercentagePerSelection: toEntryMap(data?.bonusPercentagePerSelection),
      type: 'NUM_OF_SELECTIONS',
    };

    const { bonusPercentagePerSelection: bonusPercentagePerSelectionUpdate, ...rest } = payload;
    const { bonusPercentagePerSelection, ...restUpdate } = dirtyFields;

    const updatePayload = getDirtyFieldValues(rest, restUpdate);

    if (item) {
      updateData(
        item.id,
        { ...updatePayload, bonusPercentagePerSelection: bonusPercentagePerSelectionUpdate },
        handleClose
      );
    } else {
      createData(payload, handleClose);
    }
  };

  useEffect(() => {
    if (item) {
      reset({
        ...item,
        product: 'SPORTSBOOK',
        bonusPercentagePerSelection: mapPairsToArray(item.bonusPercentagePerSelection),
        userIds: item.userIds || [],
      });
    }
  }, [item, reset]);

  const userIds = watch('userIds');

  return (
    <FormModalLayout
      onSave={() => handleSubmit(onFormSubmit)()}
      label={isEditMode ? 'Edit bonus' : 'Add new bonus'}
      onClose={handleClose}
      isEdit={isEditMode}
      showEditTabs={false}
    >
      <FormSelect
        label='Product'
        name='product'
        control={control}
        error={errors.product}
        options={BONUS_PRODUCT_TYPE_OPTIONS}
        required
        allowDeselect={false}
        disabled={isEditMode}
      />
      <FormNumberInput
        control={control}
        label='Minimal required odd'
        error={errors.minReqOdd}
        helperText={errors.minReqOdd?.message}
        name='minReqOdd'
        required
        allowDecimals
        valueAsString
      />

      <Divider />
      <Typography variant='subtitle1'>Pct. per no. of selections</Typography>
      <BonusPerSelectedCombinations control={control} />
      <Divider />

      <FormSelect
        label='Eligibility criteria'
        name='numOfSelectionsBonusEligibility'
        control={control}
        error={errors.numOfSelectionsBonusEligibility}
        options={SELECTION_BONUS_ELIGIBILITY_OPTIONS}
        required
        disabled={isEditMode}
      />
      <Divider />
      <ValidityPeriodSelect control={control} disabled />
      <FormSelect
        control={control}
        name='status'
        label='Status'
        options={BONUS_STATUS_OPTIONS_ONGOING}
        required
        allowDeselect={false}
        disabled={watch('validityPeriod') === 'CUSTOM' && !isEditMode}
      />
      <Divider />
      <CustomerSelect
        control={control}
        name='userIds'
        label='Applied to'
        placeholder={!!userIds.length ? '' : 'All players'}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled
      />
      <TextField
        error={!!errors.description}
        helperText={errors.description?.message}
        label='Description'
        {...register('description')}
        multiline
      />
    </FormModalLayout>
  );
};

export default NumOfSelectionsBonusForm;
